/**
 * The Dialer: place and answer calls on the eMerge phone line.
 *
 * == What a caller sees ===================================================
 *
 * One number field and a keypad, one Call button, and while a call is live
 * one panel with the four things a person reaches for: mute, hold, keypad,
 * end. Beside it, the calls made in this sitting, each one a click from
 * being dialled again. Nothing else. It is a phone, not a dashboard.
 *
 * == What it deliberately is not ========================================
 *
 * It dials one number, when a person presses Call, and never on its own. No
 * list is worked through automatically, no call is started by a timer, and
 * nothing is recorded. The call log lives in this page's memory and is gone
 * when the tab closes; a phone number is never written to browser storage.
 *
 * == Where the phone is ==================================================
 *
 * Amazon Connect's own panel, embedded on the right, owns sign-in, the
 * microphone and the audio. It stays visible, because the first sign-in
 * and the browser's permission prompts appear there and a hidden panel
 * cannot be clicked. The controls on the left drive it through
 * `connectPhone.ts`; the server decides only whether this page may open.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Phone } from 'lucide-react';
import { rememberWorkspace } from '../../services/workspacesApi';
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';

import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { confirmSignOutWithUnsent } from '../../config/authRoutes';
import { DIALER_LOGIN_PATH, dialerLoginNav } from './DialerLogin';
import { getLine, LineAnswer, LineConfig } from './dialerApi';
import {
  AgentView, CallStatus, CallView, Colleague, TransferView, PhoneController, PhoneProblem, isBraveBrowser, loadStreams, startPhone,
} from './connectPhone';
import { PROBLEM_TEXT, checkNumber, cleanTyped, formatForDisplay } from './phoneNumber';
import './dialer.css';

// 'separate': the embedded panel could not load, and the person moved the
// phone to Amazon Connect's own window instead.
type PhaseKind = 'idle' | 'starting' | 'ready' | 'separate';

interface LogEntry {
  key: string;
  number: string | null;
  inbound: boolean;
  startedAt: number;
  connectedAt: number | null;
  endedAt: number | null;
  outcome: 'Live' | 'Completed' | 'Transferred' | 'No answer' | 'Missed' | 'Declined' | 'Failed';
}

const KEYS: ReadonlyArray<{ d: string; sub: string }> = [
  { d: '1', sub: '' }, { d: '2', sub: 'ABC' }, { d: '3', sub: 'DEF' },
  { d: '4', sub: 'GHI' }, { d: '5', sub: 'JKL' }, { d: '6', sub: 'MNO' },
  { d: '7', sub: 'PQRS' }, { d: '8', sub: 'TUV' }, { d: '9', sub: 'WXYZ' },
  { d: '*', sub: '' }, { d: '0', sub: '+' }, { d: '#', sub: '' },
];

const STATUS_TEXT: Record<CallStatus, string> = {
  incoming: 'Incoming call',
  dialing: 'Calling…',
  connected: 'Connected',
  'on-hold': 'On hold',
  ended: 'Call ended',
  missed: 'Missed call',
  failed: 'Call didn’t go through',
};

const PROBLEM_MESSAGE: Record<PhoneProblem, string> = {
  AUTH_REQUIRED: 'Sign in to Amazon Connect to use the phone. Use the sign-in window, or open it below.',
  ACCESS_DENIED: 'Your Amazon Connect user can’t use the phone panel. Ask an administrator to check your Connect account.',
  UNREACHABLE: 'The phone panel didn’t load on this page. This is usually the browser blocking it, not your internet connection.',
  MICROPHONE: 'The phone needs your microphone. Allow microphone access for this site in the browser, then try the call again.',
  BROWSER: 'This browser can’t run the phone. Use a current version of Chrome or Edge.',
  CALL_FAILED: 'The call didn’t go through.',
};

function transferText(t: TransferView): string {
  switch (t.stage) {
    case 'ringing': return `Ringing ${t.name}… The caller is on hold.`;
    case 'talking': return `You’re talking with ${t.name}. The caller is on hold and can’t hear you.`;
    case 'joined': return `${t.name}, the caller and you are all on the call.`;
    default: return `${t.name} is on hold.`;
  }
}

type ColleagueList = Colleague[] | 'loading' | { error: string };

/** The Dialer is one screen: a phone. Its sidebar says so and nothing more (it keeps its own bar, so no workspace menu). */
const SIDEBAR_GROUPS: SidebarGroup[] = [
  { key: 'dialer', label: 'Dialer', items: [{ key: 'phone', label: 'Phone', icon: Phone, href: '/portal/dialer', active: true }] },
];

const LIVE: ReadonlySet<CallStatus> = new Set<CallStatus>(['incoming', 'dialing', 'connected', 'on-hold']);

function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(h ? 2 : 1, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function timeOfDay(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);
  return now;
}

export default function DialerShell(): JSX.Element {
  const { user } = useAuth();
  // The route guards read this session, so signing out goes through it.
  const { logout } = useUser();

  // Remembered as the last workspace used, so the next sign-in lands here.
  useEffect(() => { rememberWorkspace('DIALER'); }, []);

  const [answer, setAnswer] = useState<LineAnswer | null>(null);
  const [streamsState, setStreamsState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [phase, setPhase] = useState<PhaseKind>('idle');
  const [agent, setAgent] = useState<AgentView | null>(null);
  const [call, setCall] = useState<CallView | null>(null);
  const [muted, setMuted] = useState(false);
  const [problem, setProblem] = useState<{ kind: PhoneProblem; detail?: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [touched, setTouched] = useState(false);
  const [showTones, setShowTones] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [colleagues, setColleagues] = useState<ColleagueList>('loading');
  const [busy, setBusy] = useState(false);
  const [slowSignIn, setSlowSignIn] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const phoneRef = useRef<PhoneController | null>(null);
  const signInWindow = useRef<Window | null>(null);
  const lastCallKey = useRef<string | null>(null);
  const declined = useRef<Set<string>>(new Set());
  const transferred = useRef<Set<string>>(new Set());

  const line: LineConfig | null = answer?.kind === 'ready' ? answer.line : null;
  const check = useMemo(() => checkNumber(typed), [typed]);
  const live = !!call && LIVE.has(call.status);
  const current = log.find((e) => e.key === lastCallKey.current) || null;
  const now = useNow(live);
  const brave = useMemo(() => isBraveBrowser(), []);

  // ── The line, and the phone software, as soon as the page opens ─────────
  useEffect(() => {
    let gone = false;
    getLine().then((a) => { if (!gone) setAnswer(a); });
    return () => { gone = true; };
  }, []);

  useEffect(() => {
    if (!line) return;
    let gone = false;
    // Loaded ahead of the click, so pressing Start can open Amazon Connect's
    // sign-in window inside the same gesture a popup blocker allows.
    loadStreams().then(
      () => { if (!gone) setStreamsState('loaded'); },
      () => { if (!gone) setStreamsState('failed'); },
    );
    return () => { gone = true; };
  }, [line]);

  // ── The call log follows the call ──────────────────────────────────────
  const onCall = useCallback((c: CallView | null) => {
    setCall(c);
    if (!c) { setShowTones(false); setShowTransfer(false); setMuted(false); return; }
    if (!LIVE.has(c.status)) setShowTransfer(false);
    const key = c.id || `call-${Date.now()}`;
    const t = Date.now();
    setLog((prev) => {
      const i = prev.findIndex((e) => e.key === key);
      const base: LogEntry = i >= 0 ? prev[i] : {
        key, number: c.number, inbound: c.inbound, startedAt: t, connectedAt: null, endedAt: null, outcome: 'Live',
      };
      const next: LogEntry = { ...base, number: c.number || base.number };
      if ((c.status === 'connected' || c.status === 'on-hold') && !next.connectedAt) next.connectedAt = t;
      if (!LIVE.has(c.status) && next.outcome === 'Live') {
        next.endedAt = t;
        if (c.status === 'missed') next.outcome = 'Missed';
        else if (c.status === 'failed') next.outcome = 'Failed';
        else if (declined.current.has(key)) next.outcome = 'Declined';
        else if (transferred.current.has(key)) next.outcome = 'Transferred';
        else if (next.connectedAt) next.outcome = 'Completed';
        else next.outcome = next.inbound ? 'Missed' : 'No answer';
      }
      if (i >= 0) { const copy = prev.slice(); copy[i] = next; return copy; }
      return [next, ...prev].slice(0, 50);
    });
    lastCallKey.current = key;
    if (c.status === 'connected') setProblem((p) => (p?.kind === 'CALL_FAILED' || p?.kind === 'MICROPHONE' ? null : p));
  }, []);

  const start = useCallback(() => {
    if (!line || !panelRef.current || phoneRef.current) return;
    const connect = (window as unknown as { connect?: unknown }).connect;
    if (!connect) { setStreamsState('failed'); return; }
    setPhase('starting');
    setProblem(null);
    phoneRef.current = startPhone(connect, panelRef.current, line, {
      onAgent: (a) => {
        setAgent(a);
        setPhase('ready');
        setProblem((p) => (p && (p.kind === 'AUTH_REQUIRED' || p.kind === 'UNREACHABLE') ? null : p));
        if (signInWindow.current && !signInWindow.current.closed) signInWindow.current.close();
        signInWindow.current = null;
      },
      onCall,
      onMute: setMuted,
      onProblem: (kind, detail) => setProblem({ kind, detail }),
    });
  }, [line, onCall]);

  useEffect(() => {
    if (phase !== 'starting') { setSlowSignIn(false); return undefined; }
    const t = window.setTimeout(() => setSlowSignIn(true), 12000);
    return () => window.clearTimeout(t);
  }, [phase]);

  const openSignIn = useCallback(() => {
    if (!line) return;
    const w = window.open(line.ccpUrl, 'emerge-connect-sign-in', 'width=420,height=640,top=80,left=80');
    signInWindow.current = w;
    if (!w) setNotice('The browser blocked the sign-in window. Allow pop-ups for this site, then press Open sign-in again.');
  }, [line]);

  // The embedded panel can't load (a browser blocking it). Amazon Connect's
  // own window works in the same browser, so offer that, and stop the
  // embedded panel retrying behind it.
  const openSeparatePhone = useCallback(() => {
    if (!line) return;
    const w = window.open(line.ccpUrl, 'emerge-connect-phone', 'width=420,height=680,top=60,left=60');
    if (!w) {
      setNotice('The browser blocked the phone window. Allow pop-ups for this site, then try again.');
      return;
    }
    phoneRef.current?.stop();
    setPhase('separate');
    setProblem(null);
    setNotice(null);
  }, [line]);

  // ── Acts ────────────────────────────────────────────────────────────────
  const act = useCallback(async (fn: () => Promise<void>, failurePrefix: string) => {
    setBusy(true);
    setNotice(null);
    try { await fn(); } catch (e) {
      setNotice(`${failurePrefix} ${e instanceof Error ? e.message : ''}`.trim());
    } finally { setBusy(false); }
  }, []);

  const placeCall = useCallback((input?: string) => {
    const value = input ?? typed;
    const c = checkNumber(value);
    setTouched(true);
    if (input !== undefined) setTyped(input);
    if (!c.ok || !phoneRef.current || live) return;
    const phone = phoneRef.current;
    void act(async () => {
      if (call) await phone.finish();
      await phone.dial(c.e164);
    }, 'The call couldn’t start.');
  }, [typed, live, call, act]);

  const openTransfer = () => {
    if (showTransfer) { setShowTransfer(false); return; }
    setShowTones(false);
    setShowTransfer(true);
    setColleagues('loading');
    phoneRef.current?.colleagues().then(
      (list) => setColleagues(list),
      (e) => setColleagues({ error: e instanceof Error ? e.message : 'The transfer list didn’t load.' }),
    );
  };
  const transferTo = (id: string) => phoneRef.current && act(async () => {
    await phoneRef.current!.transferTo(id);
    setShowTransfer(false);
  }, 'The transfer couldn’t start.');
  const completeTransfer = () => phoneRef.current && call && act(async () => {
    transferred.current.add(call.id);
    try { await phoneRef.current!.completeTransfer(); } catch (e) { transferred.current.delete(call.id); throw e; }
  }, 'The transfer couldn’t be completed.');
  const joinAll = () => phoneRef.current && act(() => phoneRef.current!.joinAll(), 'The calls couldn’t be joined.');
  const cancelTransfer = () => phoneRef.current && act(() => phoneRef.current!.cancelTransfer(), 'The transfer couldn’t be cancelled.');

  const hangUp = () => phoneRef.current && act(() => phoneRef.current!.hangUp(), 'The call couldn’t be ended.');
  const toggleHold = () => phoneRef.current && call && act(() => phoneRef.current!.setHold(call.status !== 'on-hold'), 'Hold didn’t work.');
  const toggleMute = () => { try { phoneRef.current?.setMuted(!muted); } catch (e) { setNotice(e instanceof Error ? e.message : 'Mute didn’t work.'); } };
  const answerCall = () => phoneRef.current && act(() => phoneRef.current!.answer(), 'The call couldn’t be answered.');
  const declineCall = () => {
    if (!phoneRef.current || !call) return;
    declined.current.add(call.id);
    void act(() => phoneRef.current!.decline(), 'The call couldn’t be declined.');
  };
  const finish = () => phoneRef.current && act(() => phoneRef.current!.finish(), 'The phone couldn’t be reset.');
  const setTaking = (on: boolean) => phoneRef.current && act(() => phoneRef.current!.setTakingCalls(on), 'Your status didn’t change.');

  const pressKey = (d: string) => {
    if (live && call && (call.status === 'connected' || call.status === 'on-hold') && showTones) {
      void phoneRef.current?.sendDigit(d).catch(() => setNotice('That tone didn’t send.'));
      return;
    }
    if (live) return;
    setTyped((t) => cleanTyped(t + d));
  };

  const signOut = async () => {
    if (live) { setNotice('End the call before signing out.'); return; }
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    await logout();
    // A full page load: it clears the other auth state too, and the phone
    // panel with it. Back to the Dialer's own sign-in, not the portal's.
    dialerLoginNav.open(DIALER_LOGIN_PATH);
  };

  // ── Screens before the phone ────────────────────────────────────────────
  const shell = (body: React.ReactNode) => (
    <div className="dl">
      <WorkspaceLayout workspace="DIALER" navLabel="Dialer views" groups={SIDEBAR_GROUPS} className="min-h-svh">
      <header className="dl-bar">
        <div className="dl-bar-inner">
          <div className="dl-brand">
            <SidebarTrigger />
            <span className="dl-mark" aria-hidden="true">e</span>
            <div>
              <div className="dl-org">eMerge</div>
              <h1 className="dl-title">Dialer</h1>
            </div>
          </div>
          <div className="dl-bar-right">
            {phase === 'ready' && agent && (
              <span className={`dl-pill ${agent.takingCalls ? 'is-on' : ''}`} aria-live="polite">
                <span className="dl-dot" aria-hidden="true" />
                {live ? 'On a call' : agent.stateName}
              </span>
            )}
            {user?.email && <span className="dl-who">{user.email}</span>}
            <button type="button" className="dl-link" onClick={signOut}>Sign out</button>
          </div>
        </div>
      </header>
      <main className="dl-main">{body}</main>
      </WorkspaceLayout>
    </div>
  );

  if (!answer) {
    return shell(
      <section className="dl-card dl-wide" aria-busy="true">
        <span className="dl-sr-only" role="status">Loading the Dialer…</span>
        <div className="dl-skel" /><div className="dl-skel" /><div className="dl-skel short" />
      </section>,
    );
  }
  if (answer.kind === 'no-access') {
    return shell(
      <section className="dl-card dl-wide dl-state">
        <h2>You don’t have access to the Dialer</h2>
        <p>Ask an eMerge administrator to give you access, then reload this page.</p>
      </section>,
    );
  }
  if (answer.kind === 'not-configured') {
    return shell(
      <section className="dl-card dl-wide dl-state">
        <h2>The phone line isn’t set up here</h2>
        <p>Calls can only be placed from the live eMerge portal. Nothing on this page can dial.</p>
      </section>,
    );
  }
  if (answer.kind === 'error') {
    return shell(
      <section className="dl-card dl-wide dl-state">
        <h2>The Dialer didn’t load</h2>
        <p>{answer.message}</p>
      </section>,
    );
  }

  // ── The phone ──────────────────────────────────────────────────────────
  const ready = phase === 'ready';
  const canCall = ready && check.ok && !live && !busy;
  const since = current?.connectedAt ?? current?.startedAt ?? null;

  return shell(
    <div className="dl-grid">
      <section className="dl-card dl-phone" aria-labelledby="dl-phone-h">
        <div className="dl-card-head">
          <h2 id="dl-phone-h">Phone</h2>
          <p className="dl-quiet">
            {line?.callerId ? <>Calls show <strong>{formatForDisplay(line.callerId)}</strong> as the caller ID.</> : 'Calls use the eMerge phone line.'}
            {line?.recording === 'OFF' && ' Calls are not recorded.'}
          </p>
        </div>

        {phase === 'idle' && (
          <div className="dl-start">
            {brave && (
              <p className="dl-alert dl-warn">
                <strong>You’re using Brave.</strong> Before you start, click the Brave lion icon in the
                address bar and turn Shields <strong>off</strong> for this site, or the phone panel won’t load.
              </p>
            )}
            <p>
              Start the phone to make calls. The first time, Amazon Connect opens a sign-in window;
              use the Connect username and password you were given. The browser will then ask to use
              your microphone. Allow it.
            </p>
            {streamsState === 'failed' ? (
              <p className="dl-alert" role="alert">The phone software didn’t load. Reload the page to try again.</p>
            ) : (
              <button type="button" className="dl-btn dl-primary dl-big" onClick={start} disabled={streamsState !== 'loaded'}>
                {streamsState === 'loaded' ? 'Start phone' : 'Loading phone…'}
              </button>
            )}
          </div>
        )}

        {phase === 'starting' && problem?.kind !== 'UNREACHABLE' && (
          <div className="dl-start" role="status">
            <p><strong>Connecting to the phone line…</strong></p>
            <p className="dl-quiet">Sign in to Amazon Connect in the window that opened.</p>
            {(slowSignIn || problem?.kind === 'AUTH_REQUIRED') && (
              <p>
                No sign-in window? The browser may have blocked it. Allow pop-ups for this site, or{' '}
                <button type="button" className="dl-link" onClick={openSignIn}>open sign-in</button>.
              </p>
            )}
          </div>
        )}

        {problem && (
          <p className="dl-alert" role="alert">
            {PROBLEM_MESSAGE[problem.kind]}
            {problem.kind === 'CALL_FAILED' && problem.detail ? ` (${problem.detail})` : ''}
            {problem.kind === 'AUTH_REQUIRED' && phase !== 'starting' && (
              <> <button type="button" className="dl-link" onClick={openSignIn}>Open sign-in</button></>
            )}
          </p>
        )}
        {problem?.kind === 'UNREACHABLE' && phase !== 'separate' && (
          <div className="dl-start" data-testid="blocked-panel-help">
            {brave ? (
              <p>
                <strong>You’re using Brave.</strong> Click the Brave lion icon in the address bar, turn
                Shields <strong>off</strong> for this site, then{' '}
                <button type="button" className="dl-link" onClick={() => window.location.reload()}>reload the page</button>.
              </p>
            ) : (
              <p>
                Privacy settings or an ad blocker can block it. Allow this site in them, or use Chrome or
                Edge, then{' '}
                <button type="button" className="dl-link" onClick={() => window.location.reload()}>reload the page</button>.
              </p>
            )}
            <p className="dl-quiet">Need to call right now? Amazon Connect’s own window works in this browser.</p>
            <button type="button" className="dl-btn dl-primary" onClick={openSeparatePhone}>
              Use the phone in its own window
            </button>
          </div>
        )}

        {phase === 'separate' && (
          <div className="dl-start" role="status">
            <p><strong>The phone is open in its own window.</strong></p>
            <p>
              Make and answer calls there: type the number on its keypad and press Call. Keep that window open
              while you work. Calls made there don’t appear in the list on this page.
            </p>
            <div className="dl-row">
              <button type="button" className="dl-btn dl-grow" onClick={openSeparatePhone}>Show the phone window</button>
              <button type="button" className="dl-btn dl-grow" onClick={() => window.location.reload()}>Try this page again</button>
            </div>
          </div>
        )}

        {notice && <p className="dl-alert dl-warn" role="alert">{notice}</p>}

        {ready && (
          <>
            {call && call.status === 'incoming' ? (
              <div className="dl-call is-incoming" aria-live="assertive">
                <div className="dl-call-label">Incoming call</div>
                <div className="dl-call-number">{formatForDisplay(call.number)}</div>
                <div className="dl-row">
                  <button type="button" className="dl-btn dl-primary dl-grow" onClick={answerCall} disabled={busy}>Answer</button>
                  <button type="button" className="dl-btn dl-danger dl-grow" onClick={declineCall} disabled={busy}>Decline</button>
                </div>
              </div>
            ) : call ? (
              <div className={`dl-call is-${call.status}`} aria-live="polite">
                <div className="dl-call-label">{STATUS_TEXT[call.status]}{call.inbound ? ' · incoming' : ''}</div>
                <div className="dl-call-number">{formatForDisplay(call.number)}</div>
                <div className="dl-call-time" aria-label="Call time">
                  {live && since ? clock(now - since) : current?.endedAt && current.connectedAt ? clock(current.endedAt - current.connectedAt) : '—'}
                </div>
                {live && call.transfer ? (
                  <div className="dl-transfer" data-testid="transfer-panel">
                    <div className="dl-transfer-h">Transfer</div>
                    <p aria-live="polite">{transferText(call.transfer)}</p>
                    <button type="button" className="dl-btn dl-primary dl-big" onClick={completeTransfer} disabled={busy}>
                      Complete transfer
                    </button>
                    <p className="dl-quiet">You leave the call. The caller stays on the line with {call.transfer.name}.</p>
                    <div className="dl-row">
                      {call.transfer.stage === 'talking' && (
                        <button type="button" className="dl-btn dl-grow" onClick={joinAll} disabled={busy}>Join all three</button>
                      )}
                      <button type="button" className="dl-btn dl-grow" onClick={cancelTransfer} disabled={busy}>
                        {call.transfer.stage === 'joined' ? `Remove ${call.transfer.name}` : 'Cancel transfer'}
                      </button>
                    </div>
                  </div>
                ) : live ? (
                  <>
                    <div className="dl-controls">
                      <button type="button" className={`dl-ctl ${muted ? 'is-active' : ''}`} aria-pressed={muted} onClick={toggleMute}
                        disabled={call.status === 'dialing'}>
                        {muted ? 'Unmute' : 'Mute'}
                      </button>
                      <button type="button" className={`dl-ctl ${call.status === 'on-hold' ? 'is-active' : ''}`} aria-pressed={call.status === 'on-hold'}
                        onClick={toggleHold} disabled={busy || call.status === 'dialing'}>
                        {call.status === 'on-hold' ? 'Resume' : 'Hold'}
                      </button>
                      <button type="button" className={`dl-ctl ${showTones ? 'is-active' : ''}`} aria-pressed={showTones}
                        onClick={() => setShowTones((v) => !v)} disabled={call.status === 'dialing'}>
                        Keypad
                      </button>
                      <button type="button" className={`dl-ctl ${showTransfer ? 'is-active' : ''}`} aria-pressed={showTransfer}
                        onClick={openTransfer} disabled={busy || call.status !== 'connected'}>
                        Transfer
                      </button>
                    </div>
                    {showTransfer && (
                      <div className="dl-transfer" data-testid="transfer-picker">
                        <div className="dl-transfer-h">Transfer to</div>
                        {colleagues === 'loading' ? (
                          <p className="dl-quiet" role="status">Loading colleagues…</p>
                        ) : !Array.isArray(colleagues) ? (
                          <p className="dl-alert" role="alert">{colleagues.error}</p>
                        ) : colleagues.length === 0 ? (
                          <p className="dl-quiet">No colleagues are on the transfer list. Ask an administrator to add them.</p>
                        ) : (
                          <ul className="dl-colleagues">
                            {colleagues.map((p) => (
                              <li key={p.id}>
                                <button type="button" className="dl-btn dl-colleague" onClick={() => transferTo(p.id)} disabled={busy}
                                  aria-label={`Transfer to ${p.name}`}>
                                  {p.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className="dl-quiet">
                          The caller is put on hold while it rings. Your colleague needs incoming calls turned on and must not be on another call.
                        </p>
                      </div>
                    )}
                    <button type="button" className="dl-btn dl-danger dl-big" onClick={hangUp} disabled={busy}>End call</button>
                  </>
                ) : (
                  <div className="dl-row">
                    <button type="button" className="dl-btn dl-primary dl-grow" onClick={finish} disabled={busy}>Next call</button>
                    {call.number && (
                      <button type="button" className="dl-btn dl-grow" onClick={() => placeCall(call.number || '')} disabled={busy}>Call again</button>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {(!call || (live && showTones)) && (
              <div className="dl-dial">
                {!call && (
                  <form className="dl-number" onSubmit={(e) => { e.preventDefault(); placeCall(); }}>
                    <label htmlFor="dl-number-input">Number to call</label>
                    <div className="dl-number-row">
                      <input
                        id="dl-number-input"
                        type="tel"
                        inputMode="tel"
                        autoComplete="off"
                        placeholder="(813) 555-0100"
                        value={typed}
                        onChange={(e) => { setTyped(cleanTyped(e.target.value)); setTouched(false); }}
                        onBlur={() => setTouched(true)}
                        aria-invalid={touched && !check.ok}
                        aria-describedby="dl-number-help"
                      />
                      {typed && (
                        <button type="button" className="dl-erase" aria-label="Delete last digit"
                          onClick={() => setTyped((t) => t.slice(0, -1))}>⌫</button>
                      )}
                    </div>
                    <p id="dl-number-help" className={`dl-help ${touched && !check.ok && check.problem !== 'EMPTY' ? 'is-bad' : ''} ${!check.ok && check.problem === 'EMERGENCY' ? 'is-stop' : ''}`}>
                      {check.ok ? <>Ready to call <strong>{check.display}</strong></>
                        : (check.problem === 'EMERGENCY' || (touched && check.problem !== 'EMPTY')) ? PROBLEM_TEXT[check.problem]
                          : 'US numbers only. Type or paste, or use the keypad.'}
                    </p>
                  </form>
                )}
                {live && showTones && <p className="dl-quiet dl-tones-hint">Keys send tones to the call.</p>}
                <div className="dl-keypad" role="group" aria-label={live ? 'Call keypad' : 'Keypad'}>
                  {KEYS.map((k) => (
                    <button type="button" key={k.d} className="dl-key" onClick={() => pressKey(k.d)}
                      aria-label={k.d === '*' ? 'star' : k.d === '#' ? 'pound' : k.d}>
                      <span className="dl-key-d">{k.d}</span>
                      <span className="dl-key-s" aria-hidden="true">{k.sub}</span>
                    </button>
                  ))}
                </div>
                {!call && (
                  <button type="button" className="dl-btn dl-primary dl-big" onClick={() => placeCall()} disabled={!canCall}>
                    {check.ok ? `Call ${check.display}` : 'Call'}
                  </button>
                )}
              </div>
            )}

            {agent && (
              <div className="dl-status">
                <div>
                  <div className="dl-status-h">Incoming calls</div>
                  <div className="dl-quiet">
                    {agent.takingCalls
                      ? 'On. When someone calls the eMerge line, it can ring here.'
                      : 'Off. Calls to the eMerge line won’t ring here. You can still make calls.'}
                  </div>
                </div>
                <button type="button" className="dl-btn" onClick={() => setTaking(!agent.takingCalls)} disabled={busy}>
                  {agent.takingCalls ? 'Turn off' : 'Turn on'}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="dl-card dl-log" aria-labelledby="dl-log-h">
        <div className="dl-card-head">
          <h2 id="dl-log-h">This sitting</h2>
          <p className="dl-quiet">Calls from this page since it opened. Closing the page clears the list.</p>
        </div>
        {log.length === 0 ? (
          <p className="dl-empty">No calls yet.</p>
        ) : (
          <ol className="dl-log-list">
            {log.map((e) => (
              <li key={e.key} className="dl-log-item">
                <div className="dl-log-main">
                  <div className="dl-log-number">{formatForDisplay(e.number)}</div>
                  <div className="dl-quiet">
                    {e.inbound ? 'Incoming' : 'Outgoing'} · {timeOfDay(e.startedAt)}
                    {e.outcome === 'Live' ? ' · on the call' : ` · ${e.outcome}`}
                    {e.connectedAt && e.endedAt ? ` · ${clock(e.endedAt - e.connectedAt)}` : ''}
                  </div>
                </div>
                {e.number && e.outcome !== 'Live' && (
                  <button type="button" className="dl-btn dl-small" onClick={() => placeCall(e.number || '')}
                    disabled={!ready || live || busy} aria-label={`Call ${formatForDisplay(e.number)} again`}>
                    Call
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="dl-card dl-connect" aria-labelledby="dl-connect-h">
        <div className="dl-card-head">
          <h2 id="dl-connect-h">Amazon Connect</h2>
          <p className="dl-quiet">
            The phone itself. Sign-in, microphone and audio settings appear here.
          </p>
        </div>
        <div ref={panelRef} className="dl-connect-frame" data-testid="connect-panel" />
      </section>
    </div>,
  );
}
